import React, {
  Children,
  cloneElement,
  Fragment,
  HTMLProps,
  ReactElement,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
} from "react";
import { AnimatePresence } from "framer-motion";
import { useControlledState } from "@react-stately/utils";
import { mergeProps, useLayoutEffect } from "@react-aria/utils";
import { useFloatingPosition } from "../floating-position";
import { useIsMobileMediaQuery } from "@ui/utils/hooks/is-mobile-media-query";
import { DialogContext, DialogContextValue } from "./dialog-context";
import { Popover } from "../popover";
import { Tray } from "../tray";
import { Modal } from "../modal";
import { Stacked } from "../stacked";
import { createPortal } from "react-dom";
import { createEventHandler } from "@ui/utils/dom/create-event-handler";
import {
  OffsetOptions,
  Placement,
  VirtualElement,
} from "@floating-ui/react-dom";
import { rootEl } from "@ui/root-el";
import { pointToVirtualElement } from "@ui/menu/context-menu";
import { useCallbackRef } from "@ui/utils/hooks/use-callback-ref";
import { playerOverlayState } from "@app/web-player/state/player-overlay-store";
import { useDialogStack } from "./dialog-stack";
import {
  AppEventId,
  useAppEventManager,
} from "@common/core/capacitor/app-event-manager";

type PopoverProps = {
  type: "popover";
  mobileType?: "tray" | "modal" | "stacked" | "popover";
  overlayClassName?: string;
  zIndex?: number;
  placement?: Placement;
  offset?: OffsetOptions;
  title?: string;
  stackedHeaderRight?: any;
  stackedBodyPadding?: boolean;
};
type ModalProps = {
  type: "modal" | "tray";
  mobileType?: "tray" | "modal" | "stacked" | "popover";
  overlayClassName?: string;
  zIndex?: number;
  placement?: Placement;
  title?: string;
  stackedHeaderRight?: any;
  stackedBodyPadding?: boolean;
};
type StackedProp = {
  type: "stacked";
  mobileType?: "tray" | "modal" | "stacked" | "popover";
  overlayClassName?: string;
  zIndex?: number;
  placement?: Placement;
  offset?: OffsetOptions;
  title?: string;
  stackedHeaderRight?: any;
  stackedBodyPadding?: boolean;
};
type Props<T = any> = (PopoverProps | ModalProps | StackedProp) & {
  children: [ReactElement, (ctx: DialogContextValue) => void] | ReactNode;
  disableInitialTransition?: boolean;
  onClose?: (
    value: T | undefined,
    data: { initialValue: T; valueChanged: boolean },
  ) => void;
  isDismissable?: boolean;
  isOpen?: boolean;
  onValueChange?: (value: T) => void;
  alwaysReturnCurrentValueOnClose?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  defaultIsOpen?: boolean;
  triggerRef?: RefObject<HTMLElement> | RefObject<VirtualElement>;
  moveFocusToDialog?: boolean;
  returnFocusToTrigger?: boolean;
  triggerOnHover?: boolean;
  triggerOnContextMenu?: boolean;
  handleTriggerClick?: boolean;
  value?: T;
  defaultValue?: T;
  usePortal?: boolean;
  zIndex?: number;
};
export function DialogTrigger(props: Props) {
  let {
    children,
    type,
    disableInitialTransition,
    isDismissable = true,
    moveFocusToDialog = true,
    returnFocusToTrigger = true,
    triggerOnHover = false,
    triggerOnContextMenu = false,
    handleTriggerClick = true,
    usePortal = true,
    mobileType,
    alwaysReturnCurrentValueOnClose,
    title,
    stackedHeaderRight,
    stackedBodyPadding,
  } = props;

  // for context menu we will set triggerRef to VirtualElement in "onContextMenu" event.
  // If dialog is not triggered on context menu, leave triggerRef null (unless it's passed in via props)
  // otherwise it will prevent dialog from opening in "popover" mode.
  const contextMenuTriggerRef = useRef<VirtualElement | null>(null);
  const triggerRef =
    triggerOnContextMenu && !props.triggerRef
      ? contextMenuTriggerRef
      : props.triggerRef;
  // initial value can be used to restore state to what it
  // was before opening the dialog, for example in color picker
  const initialValueRef = useRef(props.value);
  const [isOpen, setIsOpen] = useControlledState(
    props.isOpen,
    props.defaultIsOpen,
    props.onOpenChange,
  );
  const [value, setValue] = useControlledState(
    props.value,
    props.defaultValue,
    props.onValueChange,
  );

  // On small devices, show a modal or tray instead of a popover.
  const isMobile = useIsMobileMediaQuery();
  if (isMobile && type === "popover") {
    // type = mobileType || 'modal';
    type = mobileType || "stacked";
  }

  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { x, y, reference, strategy, refs } = useFloatingPosition({
    ...props,
    disablePositioning: type === "modal",
  });

  const floatingStyle =
    type === "popover"
      ? {
          position: strategy,
          top: y ?? "",
          left: x ?? "",
        }
      : {};

  const { pushDialog, popDialog, topDialog } = useDialogStack();
  const id = useId();
  const labelId = `${id}-label`;
  const descriptionId = `${id}-description`;
  const formId = `${id}-form`;

  const onClose = useCallbackRef(props.onClose);
  const close = useCallback(
    (closeValue?: any) => {
      if (
        typeof closeValue === "undefined" &&
        alwaysReturnCurrentValueOnClose
      ) {
        closeValue = value;
      }
      // if value is not provided (dialog cancel button or clicking outside of dialog), use initial value
      const finalValue =
        typeof closeValue !== "undefined"
          ? closeValue
          : initialValueRef.current;

      onClose?.(finalValue, {
        initialValue: initialValueRef.current,
        valueChanged: finalValue !== initialValueRef.current,
      });
      setIsOpen(false);
      popDialog(id);
    },
    [
      alwaysReturnCurrentValueOnClose,
      id,
      onClose,
      popDialog,
      setIsOpen,
      value,
    ],
  );

  const open = useCallback(() => {
    const overlayElement = document.querySelector(
      ".player-overlay-surface, .player-overlay-bg",
    );
    const triggerElement = triggerRef?.current;

    // Gérer les cas spécifiques selon le type de triggerElement
    if (overlayElement && triggerElement) {
      if (
        // Cas où triggerElement est un HTMLElement
        triggerElement instanceof HTMLElement &&
        !overlayElement.contains(triggerElement)
      ) {
        playerOverlayState.close(); // Fermer l'overlay si le déclencheur est hors de celui-ci
      } else if (
        // Cas où triggerElement est un VirtualElement (pas un vrai DOM Node)
        !(triggerElement instanceof HTMLElement) &&
        "getBoundingClientRect" in triggerElement
      ) {
        // Calculer si le VirtualElement est en dehors de l'overlay
        const virtualRect = triggerElement.getBoundingClientRect();
        const overlayRect = overlayElement.getBoundingClientRect();

        const isOutsideOverlay =
          virtualRect.bottom < overlayRect.top ||
          virtualRect.top > overlayRect.bottom ||
          virtualRect.right < overlayRect.left ||
          virtualRect.left > overlayRect.right;

        if (isOutsideOverlay) {
          playerOverlayState.close();
        }
      }
    }

    setIsOpen(true);
    initialValueRef.current = props.value;
    pushDialog(id);
  }, [props.value, setIsOpen, pushDialog, id]);

  const dialogProps = useMemo(() => {
    return {
      "aria-labelledby": labelId,
      "aria-describedby": descriptionId,
    };
  }, [labelId, descriptionId]);

  let Overlay: typeof Modal | typeof Tray | typeof Stacked | typeof Popover;

  if (type === "modal") {
    Overlay = Modal;
  } else if (type === "tray") {
    Overlay = Tray;
  } else if (type === "stacked") {
    Overlay = Stacked;
  } else {
    Overlay = Popover;
  }

  const contextValue: DialogContextValue = useMemo(() => {
    return {
      dialogProps,
      type,
      labelId,
      descriptionId,
      isDismissable,
      close,
      value,
      initialValue: initialValueRef.current,
      setValue,
      formId,
      title,
      stackedHeaderRight,
      stackedBodyPadding,
    };
  }, [
    close,
    descriptionId,
    dialogProps,
    formId,
    labelId,
    type,
    isDismissable,
    value,
    setValue,
    title,
    stackedHeaderRight,
    stackedBodyPadding,
  ]);

  triggerOnHover = triggerOnHover && type === "popover";

  const handleTriggerHover: HTMLProps<HTMLElement> = {
    onPointerEnter: createEventHandler((e: React.PointerEvent) => {
      open();
    }),
    onPointerLeave: createEventHandler((e: React.PointerEvent) => {
      hoverTimeoutRef.current = setTimeout(() => {
        close();
      }, 150);
    }),
  };

  const handleFloatingHover: HTMLProps<HTMLElement> = {
    onPointerEnter: createEventHandler((e: React.PointerEvent) => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    }),
    onPointerLeave: createEventHandler((e: React.PointerEvent) => {
      close();
    }),
  };

  const handleTriggerContextMenu: HTMLProps<HTMLElement> = {
    onContextMenu: createEventHandler((e: React.MouseEvent) => {
      e.preventDefault();
      contextMenuTriggerRef.current = pointToVirtualElement(
        { x: e.clientX, y: e.clientY },
        e.currentTarget,
      );
      open();
    }),
  };

  const triggerClickHandler: HTMLProps<HTMLElement> = {
    onClick: createEventHandler((e: React.MouseEvent) => {
      // prevent propagating to parent, in case floating element
      // is attached to input field and button is inside the field
      e.stopPropagation();
      if (isOpen) {
        close();
      } else {
        open();
      }
    }),
  };

  const { dialogTrigger, dialog } = extractChildren(children, contextValue);

  const dialogContent = (
    <AnimatePresence initial={!disableInitialTransition}>
      {isOpen && (
        <DialogContext.Provider value={contextValue}>
          <Overlay
            {...(triggerOnHover ? handleFloatingHover : {})}
            ref={refs.setFloating}
            triggerRef={refs.reference}
            style={floatingStyle}
            restoreFocus={returnFocusToTrigger}
            autoFocus={moveFocusToDialog}
            isOpen={isOpen}
            onClose={close}
            isDismissable={isDismissable}
            isContextMenu={triggerOnContextMenu}
            placement={props.placement}
            className={props.overlayClassName}
            title={props.title}
            stackedHeaderRight={props.stackedHeaderRight}
            stackedBodyPadding={props.stackedBodyPadding}
            zIndex={props.zIndex}
          >
            {dialog}
          </Overlay>
        </DialogContext.Provider>
      )}
    </AnimatePresence>
  );

  // position dropdown relative to provided ref, not the trigger
  useLayoutEffect(() => {
    if (triggerRef?.current && refs.reference.current !== triggerRef.current) {
      reference(triggerRef.current);
    }
  }, [reference, triggerRef?.current, refs]);

  useEffect(() => {
    return () => {
      popDialog(id);
    };
  }, [id, popDialog]);

  // handle closing by buttons
  useEffect(() => {
    if (topDialog === id && isOpen) {
      let backButtonListenerId: AppEventId;
      const handleClose = (event?: any) => {
        if (isOpen) {
          close();
        }
      };

      const setupBackButtonListener = async () => {
        backButtonListenerId = await useAppEventManager.addListener(
          "backButton",
          handleClose,
          { exclusive: true, priority: 100, platform: "android" },
        );
      };
      setupBackButtonListener();

      return () => {
        useAppEventManager.removeListenerById(backButtonListenerId);
      };
    }
  }, [topDialog, id, isOpen, close]);

  return (
    <Fragment>
      {dialogTrigger &&
        cloneElement(
          dialogTrigger,
          mergeProps(
            {
              // make sure ref specified on trigger element is not overwritten
              ...(!triggerRef && !triggerOnContextMenu
                ? { ref: reference }
                : {}),
              ...(!triggerOnContextMenu && handleTriggerClick
                ? triggerClickHandler
                : {}),
              ...(triggerOnHover ? handleTriggerHover : {}),
              ...(triggerOnContextMenu ? handleTriggerContextMenu : {}),
            },
            {
              ...dialogTrigger.props,
            },
          ),
        )}
      {usePortal
        ? rootEl && createPortal(dialogContent, rootEl)
        : dialogContent}
    </Fragment>
  );
}

function extractChildren(
  rawChildren: Props["children"],
  ctx: DialogContextValue,
) {
  const children = Array.isArray(rawChildren)
    ? rawChildren
    : Children.toArray(rawChildren);

  let dialog: any = children.length === 2 ? children[1] : children[0];
  dialog = typeof dialog === "function" ? dialog(ctx) : dialog;

  // trigger and dialog passed as children
  if (children.length === 2) {
    return {
      dialogTrigger: children[0] as ReactElement,
      dialog: dialog as ReactElement,
    };
  }

  // only dialog passed as child
  return { dialog: dialog as ReactElement };
}
